import { InputNumber as AntdInputNumber, message } from 'antd';
import * as React from 'react';
import styled from '@emotion/styled';
import { PlusOutlined, MinusOutlined } from '@ant-design/icons';
import { formatNumber, parseNumber } from 'utils/numberFormat';
import { useUnit } from '../hooks/useUnit';

export const normalformatter = (value: number | string) => formatNumber(value, '0,0');
export const normalparser = (value: string) => parseNumber(value) || 0;

interface Props extends React.ComponentProps<typeof AntdInputNumber> {
  canInput?: boolean;
  stepInfoTemplate?: string;
  maxInfoTemplate?: string;
  minInfoTemplate?: string;
  unit?: boolean;
}

export const InputNumber = (props: Props) => {
  const {
    min = 0,
    max,
    canInput = false,
    stepInfoTemplate,
    maxInfoTemplate,
    minInfoTemplate,
    value,
    onChange,
    step,
    unit = false,
    ...rest
  } = props;

  const inputRef = React.useRef<HTMLInputElement>(null!);
  const { formatter, parser } = useUnit();
  const unitProps = unit
    ? ({
        formatter,
        parser,
      } as any)
    : ({
        formatter: normalformatter,
        parser: normalparser,
      } as any);

  return (
    <Container
      canInput={canInput}
      onClick={(e) => {
        const target = e.target as any;
        !canInput && inputRef.current?.blur();
        if (
          target?.className?.includes?.('ant-input-number-handler-up') &&
          Number(value) === Number(max) &&
          maxInfoTemplate
        ) {
          message.warn(maxInfoTemplate);
        }
        if (
          target?.className?.includes?.('ant-input-number-handler-down') &&
          Number(value) === Number(min)
        ) {
          message.warn(minInfoTemplate);
        }
      }}
    >
      <div className="left">
        <MinusOutlined />
      </div>
      <div className="right">
        <PlusOutlined />
      </div>
      <AntdInputNumber
        {...unitProps}
        ref={inputRef}
        value={value}
        step={step}
        defaultValue={0}
        min={min}
        max={max}
        onBlur={(e) => {
          const _parser = unit ? parser : normalparser;
          const value = _parser(e.target.value);

          if (value < Number(min)) {
            onChange?.(min);
          } else {
            const floorValue = Math.ceil(value / Number(step)) * Number(step);
            if (max && floorValue > Number(max) && maxInfoTemplate) {
              message.warn(maxInfoTemplate);
            } else if (value !== floorValue && stepInfoTemplate) {
              message.warn(stepInfoTemplate);
            }
            if (max && floorValue > Number(max)) {
              onChange?.(max);
            } else {
              onChange?.(floorValue);
            }
          }
        }}
        onPressEnter={(e: any) => {
          e.target?.blur();
        }}
        onChange={(value) => {
          onChange?.(value);
        }}
        {...rest}
      />
    </Container>
  );
};

const Container = styled.div<{ canInput: boolean }>`
  padding-left: 32px;
  padding-right: 32px;
  position: relative;
  background: #fff;
  width: fit-content;
  .left {
    position: absolute;
    left: 0;
    top: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #d9d9d9;
    border-right: 0;
    &:hover {
      box-shadow: 0 0 0 2px var(--ant-primary-color-outline);
      border-color: var(--ant-primary-5);
    }
  }
  .right {
    position: absolute;
    right: 0;
    top: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #d9d9d9;
    border-left: 0;
    &:hover {
      box-shadow: 0 0 0 2px var(--ant-primary-color-outline);
      border-color: var(--ant-primary-5);
    }
  }
  .ant-input-number {
    border-radius: 0;
    .ant-input-number-handler-wrap {
      left: -32px !important;
      right: -32px !important;
      width: auto !important;
      opacity: 0;
      z-index: 1;
      pointer-events: none;
      .ant-input-number-handler-down {
        position: absolute;
        left: 0;
        width: 32px;
        top: 0;
        background: green;
        height: 100% !important;
        pointer-events: visible;
      }
      .ant-input-number-handler-up {
        position: absolute;
        right: 0;
        width: 32px;
        top: 0;
        background: red;
        height: 100% !important;
        pointer-events: visible;
      }
    }
    .ant-input-number-input-wrap {
      input {
        text-align: center;
        pointer-events: ${(props) => (props.canInput ? 'visible;' : 'none')};
      }
    }
  }
`;
